<div class="p-4">
  <div class="block sm:inline-block w-full sm:w-auto">
    <div class="overflow-x-auto">
      <div class="w-full sm:w-fit">

        <!-- Search + Add row -->
        <div class="flex flex-col sm:flex-row gap-3 mb-4">
          <input
            type="email"
            [(ngModel)]="searchEmail"
            (keyup.enter)="search()"
            class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-80 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            placeholder="Enter email address..."
          />
          <div class="flex flex-col sm:flex-row gap-3">
            <button
              class="bg-momentum-deep_purple text-white px-4 py-2 rounded-full flex items-center justify-center gap-2 hover:bg-momentum-royal_purple transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              [disabled]="!searchEmail.trim() || isSearching"
              (click)="search()"
            >
              @if (isSearching) {
                <fa-icon [icon]="faSpinner" class="animate-spin"></fa-icon>
              } @else {
                <fa-icon [icon]="faSearch"></fa-icon>
              }
              Search
            </button>
            <button
              class="bg-momentum-deep_purple text-white px-4 py-2 rounded-full flex items-center justify-center gap-2 hover:bg-momentum-royal_purple transition-colors w-full sm:w-auto"
              (click)="openAddPanel()"
            >
              <fa-icon [icon]="faPlus"></fa-icon>
              Add Suppression
            </button>
          </div>
        </div>

        <!-- Instructions -->
        <div class="mb-4 text-sm text-gray-500 flex flex-col gap-1">
          <span><strong class="text-agentium_gray-text">Search</strong> — Enter an email address and click Search to view all Mailgun domains where a suppression exists for that address.</span>
          <span><strong class="text-agentium_gray-text">Add</strong> — Click Add Suppression to manually suppress an email address on one or more domains.</span>
          <span><strong class="text-agentium_gray-text">Remove</strong> — Search for the email address first, then click the Remove button next to any result to lift the suppression for that domain.</span>
        </div>

        <!-- Add Suppression Panel -->
        @if (showAddPanel) {
          <div class="mb-4 rounded-lg border border-gray-200 bg-white p-5">
            <h3 class="text-base font-semibold text-agentium_gray-text mb-4">Add New Suppression</h3>

            <div class="mb-4">
              <label class="block text-sm font-medium text-agentium_gray-text mb-1" for="addEmail">
                Email Address
              </label>
              <input
                id="addEmail"
                type="email"
                [(ngModel)]="addEmail"
                class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
                placeholder="user@example.com"
              />
            </div>

            <div class="mb-5">
              <span class="block text-sm font-medium text-agentium_gray-text mb-2">Domains</span>
              <div class="flex flex-col gap-2">
                @for (domain of domainOptions; track domain.key) {
                  <label class="flex items-center gap-2 text-sm text-agentium_gray-text cursor-pointer">
                    <input
                      type="checkbox"
                      [(ngModel)]="domain.selected"
                      class="w-4 h-4 cursor-pointer text-momentum-deep_purple border-gray-300 rounded focus:ring-momentum-deep_purple"
                    />
                    {{ domain.label }}
                  </label>
                }
              </div>
            </div>

            <div class="flex flex-col sm:flex-row gap-3">
              <button
                class="bg-momentum-deep_purple text-white px-4 py-2 rounded-full flex items-center justify-center gap-2 hover:bg-momentum-royal_purple transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                [disabled]="!addEmail.trim() || !anyDomainSelected || isAdding"
                (click)="confirmAdd()"
              >
                @if (isAdding) {
                  <fa-icon [icon]="faSpinner" class="animate-spin"></fa-icon>
                }
                Add Suppression
              </button>
              <button
                class="bg-gray-200 text-agentium_gray-text px-4 py-2 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors w-full sm:w-auto"
                (click)="cancelAdd()"
              >
                Cancel
              </button>
            </div>
          </div>
        }

        <!-- Results Table -->
        @if (hasSearched) {
          @if (suppressions.length > 0) {
            <div class="rounded-lg overflow-hidden border border-gray-200 [&_tr:last-child>td]:border-b-0">
              <table class="table-auto border-collapse bg-white min-w-full">
                <thead>
                  <tr>
                    <th class="border-b p-3 text-agentium_gray-text text-left whitespace-nowrap">Email Address</th>
                    <th class="border-b p-3 text-agentium_gray-text text-left whitespace-nowrap">Domain</th>
                    <th class="border-b p-3 text-agentium_gray-text text-left whitespace-nowrap hidden sm:table-cell">Suppressed Date</th>
                    <th class="border-b p-3 text-agentium_gray-text text-center whitespace-nowrap" title="Whether an active suppression exists in the Mailgun API">In Mailgun</th>
                    <th class="border-b p-3 text-agentium_gray-text text-center whitespace-nowrap" title="Whether a record exists in the local unsubscribed_email_address table">In DB</th>
                    <th class="border-b p-3 text-agentium_gray-text text-center whitespace-nowrap w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  @for (suppression of suppressions; track suppression.domain) {
                    <tr
                      class="transition-colors"
                      [class]="suppression.inMailgun !== null && suppression.inMailgun !== suppression.inDatabase
                        ? 'bg-yellow-50 hover:bg-yellow-100'
                        : 'hover:bg-gray-50'"
                    >
                      <td class="border-b p-3 text-agentium_gray-text whitespace-nowrap">{{ suppression.emailAddress }}</td>
                      <td class="border-b p-3 text-agentium_gray-text whitespace-nowrap">{{ suppression.domain }}</td>
                      <td class="border-b p-3 text-agentium_gray-text whitespace-nowrap hidden sm:table-cell">
                        {{ suppression.timeOfUnsubscribe ? (suppression.timeOfUnsubscribe | date: "medium") : "—" }}
                      </td>
                      <!-- In Mailgun -->
                      <td class="border-b p-3 text-center">
                        @if (suppression.inMailgun === true) {
                          <span class="inline-flex items-center justify-center gap-1.5">
                            <span class="w-2 h-2 rounded-full bg-green-500"></span>
                            <span class="text-green-700 text-xs whitespace-nowrap">Yes</span>
                          </span>
                        } @else if (suppression.inMailgun === false) {
                          <span class="inline-flex items-center justify-center gap-1.5">
                            <span class="w-2 h-2 rounded-full bg-red-500"></span>
                            <span class="text-red-700 text-xs whitespace-nowrap">No</span>
                          </span>
                        } @else {
                          <span class="inline-flex items-center justify-center gap-1.5" title="Mailgun API could not be reached">
                            <span class="w-2 h-2 rounded-full bg-gray-400"></span>
                            <span class="text-gray-500 text-xs whitespace-nowrap">Unknown</span>
                          </span>
                        }
                      </td>
                      <!-- In DB -->
                      <td class="border-b p-3 text-center">
                        @if (suppression.inDatabase) {
                          <span class="inline-flex items-center justify-center gap-1.5">
                            <span class="w-2 h-2 rounded-full bg-green-500"></span>
                            <span class="text-green-700 text-xs whitespace-nowrap">Yes</span>
                          </span>
                        } @else {
                          <span class="inline-flex items-center justify-center gap-1.5">
                            <span class="w-2 h-2 rounded-full bg-red-500"></span>
                            <span class="text-red-700 text-xs whitespace-nowrap">No</span>
                          </span>
                        }
                      </td>
                      <td class="border-b p-3 text-center">
                        <button
                          class="text-red-600 hover:text-red-800 transition-colors flex items-center gap-1 mx-auto"
                          title="Remove suppression"
                          (click)="confirmRemove(suppression)"
                        >
                          <fa-icon [icon]="faTrash" size="sm"></fa-icon>
                          <span class="text-sm whitespace-nowrap">Remove</span>
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <div class="rounded-lg border border-gray-200 bg-white p-6 text-center text-gray-500 w-full">
              No suppressions found for <strong>{{ searchEmail }}</strong>.
            </div>
          }
        }

      </div>
    </div>
  </div>
</div>
